import { useFormUtils } from "@/app/hooks/useFormUtils";
import { Combobox, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";
import { Fragment, useEffect, useState } from "react";
import { Tooltip } from "react-tooltip";
import FormFieldErrors from "../../Reusable/FormFieldErrors";
import { ISearchableMultiSelectMenuAddons, ISelectMenuItemData, ISelectMenuItemDataAddOns } from "../../interface";
import Fuse from "fuse.js";
import { useMemo } from "react";

const SearchableMultiSelectMenuAddOns: React.FC<ISearchableMultiSelectMenuAddons> = ({
  onSelectItem,
  errorMessages,
  register,
  fieldError,
  showTypedErrors,
  asButton,
  renderAsButton,
  itemsClassName,
  showDropdownWhenNoData,
  defaultSelected,
  defaultSelectedIds,
  noDataDropDownContent,
  listBoxClassName,
  showTooltips,
  className,
  addAction,
  ...props
}) => {
  const { deduceFormFieldErrors, isFormFieldValid } = useFormUtils();
  const [selectedOptions, setSelectedOptions] = useState<Array<ISelectMenuItemDataAddOns>>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [query, setQuery] = useState("");
  const [justSelected, setJustSelected] = useState(false);



  const fuse = useMemo(() => {
    if (!props?.selectItems || props.selectItems.length === 0) return null;

    return new Fuse(props.selectItems, {
      keys: [
        { name: "aliases", weight: 1.2 },
        { name: "title", weight: 0.8 }
      ],
      threshold: 0.35,
      distance: 100,
      ignoreLocation: true,
      minMatchCharLength: 2,
      shouldSort: true
    });
  }, [props.selectItems]);

  const filteredItems = useMemo(() => {
    if (!query || query.trim() === "") {
      return props.selectItems;
    }
    if (!fuse) return [];

    return fuse.search(query).map((result) => result.item);
  }, [query, fuse, props.selectItems]);


  const reducedErrorMessages = deduceFormFieldErrors(errorMessages, fieldError);
  const isValid: boolean = isFormFieldValid(reducedErrorMessages);

  const handleSelectItems = (items: ISelectMenuItemData[] | any) => {
    const selectedItems = items?.filter((i) => i !== "selectAll");
    if (items?.length > 0 && items[items?.length - 1] === "selectAll" && selectAll) {
      onSelectItem([]);
      setSelectedOptions([]);
      setSelectAll(false);
    } else if (items?.length > 0 && items[0] === "selectAll" && !selectAll) {
      setSelectedOptions(props.selectItems);
      onSelectItem(props.selectItems);
      setSelectAll(true);
    } else if (items?.length > 0 && items[items?.length - 1] === "selectAll" && !selectAll) {
      setSelectedOptions(props.selectItems);
      onSelectItem(props.selectItems);
      setSelectAll(true);
    } else if (selectedItems?.length === props?.selectItems?.length) {
      setSelectedOptions(props.selectItems);
      onSelectItem(props.selectItems);
      setSelectAll(true);
    } else {
      setSelectAll(false);
      onSelectItem(selectedItems);
      setSelectedOptions(selectedItems);
    }
  };

  useEffect(() => {
    setSelectedOptions(defaultSelected);
  }, [defaultSelected]);

  useEffect(() => {
    if (props?.selectItems?.length === 0) return;
    if (defaultSelectedIds === undefined) {
      setSelectedOptions(null);
    } else {
      setSelectedOptions(props?.selectItems?.filter((i) => defaultSelectedIds.includes(i.id)));
    }
  }, [defaultSelectedIds]);

  useEffect(() => {
    if (props?.selectItems?.length > 0 && props?.defaultSelectAll === true) {
      setSelectAll(true);
      setSelectedOptions(props.selectItems);
      onSelectItem(props.selectItems);
    }
  }, [props?.selectItems?.length, props?.defaultSelectAll]);

  // useEffect(() => {
  //   if (props?.selectItems?.length > 0 && !props?.defaultSelectAll) {
  //     setSelectAll(false);
  //     setSelectedOptions(props.selectItems);
  //     onSelectItem(props.selectItems);
  //   }
  // }, [props?.selectItems?.length, props?.defaultSelectAll]);

  const showFieldErrors = showTypedErrors && showTypedErrors === true ? true : showTypedErrors === false ? false : true;

  const renderDisplay = (items: ISelectMenuItemData[]) => {
    if (!items) return "";
    if (props?.expandedInputValue && props?.expandedInputValue === true) {
      const titles = items.map((i) => i.title);
      return titles?.join(", ");
    }
    if (items?.length === 0) {
      return "";
    } else if (items?.length === 1) {
      return items[0]?.title;
    } else {
      let label = items[0]?.title ?? "";
      label = label?.length > 15 ? `${label.substring(0, 15)}..` : label;
      return `${label} , +${items.length - 1}`;
    }
  };

  const renderEmtpyState = (cascadeMenuOpen: boolean) => {
    if (props?.watchCascadeSelectMenuState) {
      props?.watchCascadeSelectMenuState(cascadeMenuOpen);
    }
    return null;
  };

  // Handler for adding custom diagnosis via Enter key
  const handleAddViaEnter = () => {
    if (addAction && query.trim()) {
      addAction.callBack(query);
      setQuery("");
    }
  };

  const handleEnterSelection = () => {
    // Prevent repeated Enter presses
    if (justSelected) return;

    // CASE 1: Fuzzy match exists
    if (filteredItems.length > 0 && query.trim()) {
      const topMatch = filteredItems[0];

      // Prevent duplicate selection
      if (!selectedOptions?.some((s) => s.id === topMatch.id)) {
        handleSelectItems([...selectedOptions, topMatch]);
      }

      setQuery("");
      setJustSelected(true);
      return;
    }

    // CASE 2: Custom add
    if (addAction && query.trim()) {
      addAction.callBack(query.trim());
      setQuery("");
      setJustSelected(true);
    }
  };



  return (
    <div style={props?.style ?? {}} className={clsx("relative", props?.containerClassName ?? "")}>
      <Combobox
        disabled={props?.disabled && props?.disabled === true ? true : false}
        value={selectedOptions ?? []}
        onChange={(items: ISelectMenuItemData[]) => {
          handleSelectItems(items);
        }}
        multiple
        nullable
      >
        {({ open }) => {
          {
            renderEmtpyState(open);
          }
          return (
            <>
              {props?.label && (
                <span className="absolute -top-4 text-xs left-1 text-[#808DA0] font-medium z-10">{props?.label}</span>
              )}
              <div className="relative  w-full">
                {!props?.isCascaded ? (
                  asButton ? (
                    <Combobox.Button>{renderAsButton}</Combobox.Button>
                  ) : (
                    <div
                      className={clsx(
                        "relative w-full cursor-default overflow-hidden rounded-lg border border-gray-300 bg-white text-left  focus:outline-none focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm",
                        !isValid && "border-none ",
                        props?.disabled && "bg-gray-100",
                      )}
                    >
                      <Combobox.Button className={"w-full"}>
                        <Combobox.Input
                          className={clsx(
                            `w-full text-left text-xs font-medium  rounded-lg py-3 pl-3 pr-10 text-black leading-5  focus:ring-0 ${className}`,
                            isValid
                              ? "text-black border-none outline-none font-medium"
                              : "text-red-600  border border-red-500  outline-red-500 font-medium placeholder-red-400",
                            props?.disabled && props?.disabled === true && "bg-gray-200",
                          )}
                          displayValue={() => query}
                          autoComplete="off"
                          placeholder={props?.placeholder ?? "Select Multiple"}
                          onChange={(event) => {
                            setQuery(event.target.value);
                            setJustSelected(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleEnterSelection();
                            }
                          }}

                        />
                      </Combobox.Button>
                    </div>
                  )
                ) : (
                  <div
                    className={clsx(
                      "relative  cursor-default overflow-hidden rounded-lg  bg-white text-left  focus:outline-none w-64 focus-visible:ring-1 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 sm:text-sm",
                      !isValid && "border-none ",
                      props?.disabled && "bg-gray-100",
                    )}
                  ></div>
                )}
                {showFieldErrors && (
                  <div className="">
                    <FormFieldErrors errors={reducedErrorMessages} />
                  </div>
                )}
                <Transition
                  show={props?.cascadeState ? props?.cascadeState : (open || query.length > 0)}
                  as={Fragment}
                  leave="transition ease-in duration-100"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                  afterLeave={() => {
                    setQuery("");
                    setJustSelected(false);
                  }}

                >
                  <Combobox.Options
                    // style={{ zIndex: 1 }}
                    className={clsx(
                      "absolute   mt-1 w-full  overflow-auto  rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none max-h-60 sm:text-sm",
                      listBoxClassName ? listBoxClassName : "",
                      props?.listZIndex ? props?.listZIndex : "z-50",
                    )}
                  >
                    {props?.withDropDownSearch && props?.withDropDownSearch === true && (
                      <Combobox.Option
                        value={null}
                        className={"w-full pl-2"}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      >
                        <div className="flex justify-start items-center">
                          <span>
                            <MagnifyingGlassIcon className="w-3 h-3" />
                          </span>
                          <input
                            onChange={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setQuery(e.target.value);
                              setJustSelected(false);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                e.stopPropagation();
                                e.nativeEvent.stopImmediatePropagation();
                                handleEnterSelection();
                              }

                            }}

                            value={query}
                            className="w-full text-black border-t-0 border-l-0 border-r-0  outline-none ring-0 border-b text-xs pl-2 pr-4 border-b-gray-200 focus:ring-0 focus:outline-none "
                            type="text"
                            placeholder="Search withing options"
                          />
                        </div>
                      </Combobox.Option>
                    )}
                    {props?.withSelectAll === true && query?.length === 0 && props.selectItems?.length > 0 && (
                      <Combobox.Option
                        className={({ selected: _l }) =>
                          clsx(
                            "relative cursor-pointer flex font-medium pr-4 pl-2 text-xs justify-between items-center select-none py-2 text-center ",
                            selectAll
                              ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                              : "text-gray-800 hover:bg-gray-800 hover:text-white",
                            itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                          )
                        }
                        // onClick={() => setSelectAll(!selectAll)}
                        value={"selectAll"}
                      >
                        <div className="w-full flex justify-start items-center">
                          <input
                            type="checkbox"
                            checked={selectAll}
                            className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                          />
                          <span
                            className={`inline-block ml-2  text-left truncate ${selectAll ? "font-medium " : "font-normal"}`}
                          >
                            {props?.selectAllTitle ? props?.selectAllTitle : "Select All"}
                          </span>
                        </div>
                      </Combobox.Option>
                    )}

                    {filteredItems?.length === 0
                      ? showDropdownWhenNoData &&
                      showDropdownWhenNoData === true && (
                        <div className="absolute border border-gray-200 shadow-md  rounded-md mt-2 w-full cursor-default select-none py-2 px-4 text-gray-700">
                          {noDataDropDownContent ? (
                            noDataDropDownContent
                          ) : (
                            <p className="text-gray-500 text-sm text-center">No Data ...</p>
                          )}
                        </div>
                      )
                      : filteredItems?.length > 0 &&
                      filteredItems?.map((item: ISelectMenuItemData) => (
                        <Combobox.Option
                          key={item.id}
                          className={({ selected: _l }) =>
                            clsx(
                              "relative cursor-pointer flex font-medium pr-4 pl-2 text-xs justify-between items-center select-none py-2 text-center ",
                              selectedOptions?.find((so) => so?.id === item.id)
                                ? "bg-gray-100 hover:bg-gray-100 text-gray-800 font-medium "
                                : "text-gray-800 hover:bg-gray-800 hover:text-white",
                              itemsClassName && itemsClassName !== "" ? itemsClassName : "",
                            )
                          }
                          data-tooltip-place="right"
                          data-tooltip-id={`option-tooltip:${register?.name}`}
                          data-tooltip-content={item.title}
                          value={item}
                        >
                          {({ selected: _l, active: _isActive }) => (
                             <div key={`option:${item.id}`} className="w-full flex justify-start items-center">
                              <input
                                type="checkbox"
                                checked={selectedOptions?.find((so) => so?.id === item?.id) ? true : false}
                                className="h-4 w-4 rounded border-gray-300 text-black focus:ring-black"
                              />
                              <div className="flex-1 min-w-0 flex items-center gap-x-2 ml-2">
                                {props?.iconURLKey && props?.iconURLKey !== "" && (
                                  <img
                                    data-tooltip-id={`icon:${register.name}`}
                                    data-tooltip-content={item?.state[props?.iconURLKey]}
                                    src={item?.state[props?.iconURLKey]}
                                    className="w-6 h-6"
                                    alt={`Icon:${item.title}`}
                                  />
                                )}
                                <span
                                  className={`inline-block text-left truncate ${selectedOptions?.find((so) => so?.id === item?.id)
                                    ? "font-medium "
                                    : "font-normal"
                                    }`}
                                >
                                  {item.title}
                                </span>
                              </div>
                            </div>
                          )}
                        </Combobox.Option>
                      ))}

                    {addAction && filteredItems?.length >= 0 && query.trim() && (
                      <div className="p-2 pt-3">
                        <Combobox.Button
                          className="text-[#0068FF] cursor-pointer text-xs hover:underline"
                          onClick={() => handleAddViaEnter()}
                        >
                          {addAction?.title} {query && <span>{`"${query}"`}</span>}
                        </Combobox.Button>
                      </div>
                    )}
                  </Combobox.Options>
                </Transition>
              </div>
            </>
          );
        }}
      </Combobox>
      <Tooltip id={`option-tooltip:${register?.name}`} style={{ zIndex: 10, fontSize: "10px" }} />
    </div>
  );
};

export default SearchableMultiSelectMenuAddOns;
